import { useEffect } from "react";
import { useQuery, type QueryKey, type UseQueryOptions } from "@tanstack/react-query";
import { useMe } from "./me.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   A query that opens on what it showed last time (Jay, 2026-10-08: Now must
   load "lightning fast").

   The last answer is kept in this browser, per person, and shown the moment
   the page opens; the real request still goes out at once and replaces it.
   Storage that is full, blocked or missing simply means no head start.
   ────────────────────────────────────────────────────────────────────────── */

const PREFIX = "signet.seen.";
const MAX_CHARS = 400_000;
/** Older than this, the last answer is not worth showing even for a moment. */
const MAX_AGE_MS = 24 * 3_600_000;

type Stored<T> = { at: number; data: T };

function read<T>(key: string): Stored<T> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Stored<T>;
    return Date.now() - stored.at < MAX_AGE_MS ? stored : null;
  } catch {
    return null;
  }
}

function write<T>(key: string, data: T, at: number): void {
  try {
    const raw = JSON.stringify({ at, data });
    if (raw.length <= MAX_CHARS) localStorage.setItem(key, raw);
  } catch {
    // Full or blocked: no head start next time, nothing else.
  }
}

export function useSeenQuery<T>(options: Omit<UseQueryOptions<T, Error, T, QueryKey>, "initialData" | "initialDataUpdatedAt"> & { queryKey: QueryKey }) {
  const { me } = useMe();
  const key = me ? `${PREFIX}${me.user.id}.${JSON.stringify(options.queryKey)}` : null;
  const stored = key ? read<T>(key) : null;
  const query = useQuery<T, Error, T, QueryKey>({
    ...options,
    ...(stored ? { initialData: stored.data, initialDataUpdatedAt: stored.at } : {}),
  });
  useEffect(() => {
    if (key && query.data !== undefined && query.dataUpdatedAt > (stored?.at ?? 0)) write(key, query.data, query.dataUpdatedAt);
  }, [key, query.data, query.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  return query;
}
